import "./style.css";
import { mountApp } from "./ui/app";
import { toast } from "./ui/toasts";

const root = document.querySelector<HTMLDivElement>("#app");
if (root) {
  mountApp(root);
} else {
  document.body.textContent = "MOPA Pixelator failed to start: missing #app root.";
}

/* ------------------------------------------------------------------ *
 * PWA platform plumbing: offline service worker + deferred install.
 *
 * Offline capability is the privacy proof — once the shell is cached the
 * app runs with the network panel empty, showing that files never leave the
 * device. Nothing here is required for the app to function; every branch is
 * feature-detected and silent on failure.
 * ------------------------------------------------------------------ */

/** The browser's deferred install event, stashed so the UI can trigger it. */
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

let deferredInstall: BeforeInstallPromptEvent | null = null;
let installDismissed = false;

/**
 * Public install controller the app shell can call. Deferred + dismissible by
 * contract: `available()` is false until the browser offers a prompt and false
 * again once used or dismissed, so the UI never nags.
 */
export interface InstallController {
  /** True when a native install prompt is available to trigger. */
  available(): boolean;
  /** Show the native install prompt; resolves to the user's choice. */
  prompt(): Promise<"accepted" | "dismissed" | "unavailable">;
  /** Permanently hide the install affordance for this session. */
  dismiss(): void;
  /** Subscribe to availability changes; returns an unsubscribe fn. */
  onChange(fn: (available: boolean) => void): () => void;
}

const installListeners = new Set<(available: boolean) => void>();

function installAvailable(): boolean {
  return !installDismissed && deferredInstall !== null;
}

function notifyInstall(): void {
  const a = installAvailable();
  for (const fn of installListeners) fn(a);
}

export const installController: InstallController = {
  available: installAvailable,
  async prompt() {
    const evt = deferredInstall;
    if (!evt || installDismissed) return "unavailable";
    deferredInstall = null; // a prompt can only be used once
    notifyInstall();
    try {
      await evt.prompt();
      const choice = await evt.userChoice;
      return choice.outcome;
    } catch {
      return "unavailable";
    }
  },
  dismiss() {
    installDismissed = true;
    deferredInstall = null;
    notifyInstall();
  },
  onChange(fn) {
    installListeners.add(fn);
    fn(installAvailable());
    return () => installListeners.delete(fn);
  },
};

// Expose on window so app.ts can wire an install affordance without importing
// main.ts (avoids a circular UI dependency). Typed via the augmentation below.
window.mopaInstall = installController;

window.addEventListener("beforeinstallprompt", (e) => {
  e.preventDefault(); // suppress the default mini-infobar; we surface it deferred
  deferredInstall = e as BeforeInstallPromptEvent;
  notifyInstall();
});

window.addEventListener("appinstalled", () => {
  deferredInstall = null;
  installDismissed = true;
  notifyInstall();
});

/* ---- Service worker registration + update flow ---- */

function promptReload(reg: ServiceWorkerRegistration): void {
  const waiting = reg.waiting;
  if (!waiting) return;
  toast("info", "Update ready — reload to get the latest version.", {
    timeoutMs: 0,
  });
  // Reload once the new worker takes control (fires after skipWaiting).
  let reloaded = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (reloaded) return;
    reloaded = true;
    window.location.reload();
  });
  waiting.postMessage({ type: "SKIP_WAITING" });
}

function watchForUpdate(reg: ServiceWorkerRegistration): void {
  // Already waiting from a previous session.
  if (reg.waiting && navigator.serviceWorker.controller) {
    promptReload(reg);
  }
  reg.addEventListener("updatefound", () => {
    const installing = reg.installing;
    if (!installing) return;
    installing.addEventListener("statechange", () => {
      if (
        installing.state === "installed" &&
        navigator.serviceWorker.controller
      ) {
        // A new version installed alongside a live one → offer the reload.
        promptReload(reg);
      }
    });
  });
}

// Skip SW in dev (Vite serves unhashed modules that would poison the cache).
const isDev = Boolean(
  (import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV,
);

if ("serviceWorker" in navigator && !isDev) {
  window.addEventListener("load", () => {
    // Built by Vite as a dedicated ESM entry emitted to the site root as
    // `sw.js`. Registered at the site scope so it controls the whole app.
    const swUrl = new URL("sw.js", document.baseURI);
    navigator.serviceWorker
      .register(swUrl, { type: "module" })
      .then((reg) => watchForUpdate(reg))
      .catch(() => {
        /* offline enhancement only — safe to ignore */
      });
  });
}

declare global {
  interface Window {
    mopaInstall?: InstallController;
  }
}
